import React from 'react';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import authService from '../services/authService';
import { 
  AuthState, 
  User, 
  LoginCredentials, 
  UpdateProfileData, 
  ChangePasswordData,
  Permission,
  Role 
} from '../types/auth';

const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Estado inicial
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: false,
        accessToken: null,
        refreshToken: null,
        tokenExpiry: null,
        rememberMe: false,

        // Ações principais
        login: async (credentials: LoginCredentials): Promise<User> => {
          set({ isLoading: true });
          
          try {
            const response = await authService.login(credentials);
            const { user, access, refresh, remember_me, token_expires_at } = response;
            
            console.log('[Auth DEBUG] Login realizado:', {
              userEmail: user?.email,
              hasAccess: !!access,
              hasRefresh: !!refresh,
              rememberMe: remember_me,
              accessLength: access?.length,
              refreshLength: refresh?.length
            });
            
            // Usar expiração real retornada pelo backend ou fallback
            const tokenExpiry = token_expires_at?.access 
              ? token_expires_at.access * 1000 
              : Date.now() + (remember_me ? 4 * 60 * 60 * 1000 : 60 * 60 * 1000); // 4h ou 1h
            
            // Configurar storage baseado em remember-me
            if (!remember_me) {
              // Para sessões temporárias: usar sessionStorage para auto-limpeza apenas no fechamento
              console.log('[Auth DEBUG] Sessão temporária - salvando em sessionStorage também');
              sessionStorage.setItem('temp-session', 'true');
            } else {
              console.log('[Auth DEBUG] Sessão persistente - mantendo em localStorage');
            }
            
            console.log('[Auth DEBUG] ✅ Tokens salvos - remember_me:', remember_me);
            
            set({ 
              user,
              isAuthenticated: true,
              accessToken: access,
              refreshToken: refresh,
              tokenExpiry,
              rememberMe: remember_me || false,
              isLoading: false 
            });
            
            return user;
          } catch (error) {
            set({ isLoading: false });
            throw error;
          }
        },

        logout: async (): Promise<void> => {
          set({ isLoading: true });
          
          try {
            await authService.logout();
          } catch (error) {
            // Ignorar erros de logout no servidor
            console.error('Erro no logout do servidor:', error);
          } finally {
            // Limpar ambos os storages para garantir limpeza completa
            localStorage.removeItem('auth-storage');
            sessionStorage.removeItem('auth-storage');
            
            // Limpar estado local sempre
            set({ 
              user: null,
              isAuthenticated: false,
              accessToken: null,
              refreshToken: null,
              tokenExpiry: null,
              rememberMe: false,
              isLoading: false 
            });
          }
        },

        updateProfile: async (data: UpdateProfileData): Promise<User> => {
          set({ isLoading: true });
          
          try {
            const updatedUser = await authService.updateProfile(data);
            
            set({ 
              user: updatedUser,
              isLoading: false 
            });
            
            return updatedUser;
          } catch (error) {
            set({ isLoading: false });
            throw error;
          }
        },

        changePassword: async (data: ChangePasswordData): Promise<void> => {
          set({ isLoading: true });
          
          try {
            await authService.changePassword(data);
            set({ isLoading: false });
          } catch (error) {
            set({ isLoading: false });
            throw error;
          }
        },

        refreshAuth: async (): Promise<string> => {
          const { refreshToken, rememberMe } = get();
          
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }
          
          try {
            const newAccessToken = await authService.refreshToken();
            // Usar duração baseada em remember-me
            const tokenExpiry = Date.now() + (rememberMe ? 4 * 60 * 60 * 1000 : 60 * 60 * 1000); // 4h ou 1h
            
            set({ 
              accessToken: newAccessToken,
              tokenExpiry 
            });
            
            return newAccessToken;
          } catch (error) {
            // Se refresh falhar, fazer logout
            get().logout();
            throw error;
          }
        },

        checkAuth: async (): Promise<boolean> => {
          const { accessToken, refreshToken, tokenExpiry } = get();
          
          if (!accessToken || !refreshToken) {
            return false;
          }
          
          // Se token ainda está válido
          if (tokenExpiry && tokenExpiry > Date.now()) {
            return true;
          }
          
          // Tentar renovar token
          try {
            await get().refreshAuth();
            return true;
          } catch (error) {
            return false;
          }
        },

        // Utilitários de permissão
        hasPermission: (permission: string): boolean => {
          const { user } = get();
          if (!user) return false;
          
          // Superuser tem todas as permissões
          if (user.is_superuser) return true;
          
          return user.permissions?.includes(permission) || false;
        },

        hasRole: (role: string): boolean => {
          const { user } = get();
          if (!user) return false;
          
          return user.groups?.includes(role) || false;
        },

        isAdmin: (): boolean => {
          const { user, hasRole, hasPermission } = get();
          if (!user) return false;
          
          return user.is_superuser || 
                 user.is_staff || 
                 hasRole(Role.ADMIN) || 
                 hasPermission(Permission.ADMIN_ACCESS);
        },

        getDisplayName: (): string => {
          const { user } = get();
          if (!user) return '';
          
          const fullName = `${user.first_name} ${user.last_name}`.trim();
          return fullName || user.email || 'Usuário';
        },

        getInitials: (): string => {
          const { user } = get();
          if (!user) return '';
          
          const firstInitial = user.first_name?.charAt(0) || '';
          const lastInitial = user.last_name?.charAt(0) || '';
          
          if (firstInitial && lastInitial) {
            return `${firstInitial}${lastInitial}`.toUpperCase();
          }
          
          return user.email?.charAt(0).toUpperCase() || 'U';
        },

        // Estado interno
        setUser: (user: User | null) => {
          set({ user, isAuthenticated: !!user });
        },

        setLoading: (isLoading: boolean) => {
          set({ isLoading });
        },

        setTokens: (access: string, refresh: string) => {
          const tokenExpiry = Date.now() + (15 * 60 * 1000);
          set({ 
            accessToken: access, 
            refreshToken: refresh,
            tokenExpiry 
          });
        },

        clearAuth: () => {
          // Limpar ambos os storages
          localStorage.removeItem('auth-storage');
          sessionStorage.removeItem('auth-storage');
          
          set({
            user: null,
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
            tokenExpiry: null,
            rememberMe: false,
            isLoading: false
          });
        },

        initialize: async (): Promise<void> => {
          // Aguardar hidratação do Zustand persist
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const { accessToken, refreshToken, user } = get();
          
          console.log('[Auth DEBUG] Initialize após hydrate:', {
            hasAccessToken: !!accessToken,
            hasRefreshToken: !!refreshToken,
            hasUser: !!user,
            accessTokenLength: accessToken?.length,
            userEmail: user?.email,
            localStorage: !!localStorage.getItem('auth-storage')
          });
          
          // Forçar reload do localStorage se Zustand não hidratou
          if (!accessToken) {
            const storage = localStorage.getItem('auth-storage');
            console.log('[Auth DEBUG] localStorage raw:', storage?.substring(0, 200) + '...');
            if (storage) {
              try {
                const parsed = JSON.parse(storage);
                console.log('[Auth DEBUG] localStorage parsed keys:', Object.keys(parsed));
                console.log('[Auth DEBUG] localStorage.state keys:', parsed.state ? Object.keys(parsed.state) : 'no state');
                console.log('[Auth DEBUG] localStorage accessToken exists:', !!parsed.state?.accessToken);
                
                if (parsed.state?.accessToken) {
                  console.log('[Auth DEBUG] ✅ Recuperando tokens do localStorage!');
                  set({
                    accessToken: parsed.state.accessToken,
                    refreshToken: parsed.state.refreshToken,
                    user: parsed.state.user,
                    isAuthenticated: true,
                    isInitialized: true
                  });
                  return;
                } else {
                  console.log('[Auth DEBUG] ❌ accessToken não encontrado na estrutura');
                }
              } catch (e) {
                console.error('[Auth DEBUG] Erro ao parsear localStorage:', e);
              }
            }
          }
          
          // Se tem token, manter logado
          if (accessToken) {
            console.log('[Auth DEBUG] Mantendo sessão logada');
            set({ 
              isAuthenticated: true,
              isInitialized: true 
            });
            return;
          }
          
          // Sem token = não autenticado
          console.log('[Auth DEBUG] Sem tokens - não autenticado');
          set({ 
            isAuthenticated: false,
            isInitialized: true 
          });
        },
      }),
      {
        name: 'auth-storage',
        partialize: (state) => ({
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          tokenExpiry: state.tokenExpiry,
          user: state.user,
          rememberMe: state.rememberMe,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Hook para auto-inicialização
export const useAuthInitialization = () => {
  const { initialize, isInitialized } = useAuthStore();
  
  React.useEffect(() => {
    if (!isInitialized) {
      initialize();
    }
  }, [initialize, isInitialized]);
  
  return isInitialized;
};

// Hook para auto-refresh de tokens (menos agressivo)
export const useTokenRefresh = () => {
  const { isAuthenticated, tokenExpiry, rememberMe, refreshAuth } = useAuthStore();
  
  React.useEffect(() => {
    if (!isAuthenticated || !tokenExpiry) return;
    
    // Renovar token com margem baseada no tipo de sessão
    const refreshMargin = rememberMe 
      ? 10 * 60 * 1000  // Remember-me: 10 minutos antes (token de 4h)
      : 5 * 60 * 1000;  // Sessão normal: 5 minutos antes (token de 1h)
    
    const refreshTime = tokenExpiry - Date.now() - refreshMargin;
    
    if (refreshTime <= 0) {
      // Token já expirado ou prestes a expirar, renovar imediatamente
      refreshAuth().catch(console.error);
      return;
    }
    
    // Agendar renovação
    const timeout = setTimeout(() => {
      // Verificar se ainda está autenticado antes de renovar (evita refresh desnecessário)
      if (useAuthStore.getState().isAuthenticated) {
        refreshAuth().catch(console.error);
      }
    }, refreshTime);
    
    return () => clearTimeout(timeout);
  }, [isAuthenticated, tokenExpiry, rememberMe, refreshAuth]);
};

export default useAuthStore;